import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiInspections } from "../api/inspections";
import { Loader } from 'google-maps';
import FSButton from "../components/Buttons/FSButton";
import * as PropTypes from "prop-types";
import { apiInspectors } from "../api/inspectors";
import { DateTime } from "luxon";
import { parseDateTimeAndChangeToLocalTimezone } from "../utilities/dateTime";
import { getRoles } from "../api/core/provider";
import { Dialog, DialogActions, DialogContent } from "@material-ui/core";
import DialogTitle from "@material-ui/core/DialogTitle";
import ColorKey from "../components/Misc/ColorKey";
import { encodeSVG, getTextColorForBackground } from "../utilities/colours";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { config } from "../core";
import BatchContactDialog from "../components/Modal/BatchContactDialog";
import { isCompleted, inspectionAge, ageGroup, toggleSelection } from "./openMapFilters";

const formatDate = date => {
  const d = new Date(date);
  return d.toLocaleDateString();
};

function getInitials(InspectorName) {
  return InspectorName.split(" ").map((n) => n[0]).join("");
}

function InspectionsTable({ inspections, selectedPolicyNos, selectInspection, mouseOverInspection, mouseOutInspection, getColorForInspection, hasMissedAppointment }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedInspections = useMemo(() => {
    if (!inspections || !sortKey) {return inspections;}
    const sorted = [...inspections].sort((a, b) => {
      let aVal, bVal;
      switch (sortKey) {
      case "policy": aVal = a.PolicyNo; bVal = b.PolicyNo; break;
      case "insured": aVal = a.Insured; bVal = b.Insured; break;
      case "address": {
        const cmp = a.City.localeCompare(b.City);
        if (cmp !== 0) {return cmp;}
        const streetA = a.Address.replace(/^\d+\s*/, "");
        const streetB = b.Address.replace(/^\d+\s*/, "");
        aVal = streetA; bVal = streetB; break;
      }
      case "type": aVal = a.Type; bVal = b.Type; break;
      case "status": {
        const sCmp = a.PrettyStatus.localeCompare(b.PrettyStatus);
        if (sCmp !== 0) {return sCmp;}
        const aAge = inspectionAge(a);
        const bAge = inspectionAge(b);
        return aAge - bAge;
      }
      case "assign": aVal = a.InspectorName || ""; bVal = b.InspectorName || ""; break;
      default: return 0;
      }
      if (typeof aVal === "string") {return aVal.localeCompare(bVal);}
      return aVal - bVal;
    });
    return sortDir === "desc" ? sorted.reverse() : sorted;
  }, [inspections, sortKey, sortDir]);

  const sortIndicator = (key) => {
    if (sortKey !== key) {return "";}
    return sortDir === "asc" ? " ▲" : " ▼";
  };

  const thStyle = "tw-cursor-pointer tw-select-none hover:tw-bg-pink-200";

  return (
    <table className={"table planner-inspections-table open-map-table tw-flex-grow-0 text-sm"}>
      <thead>
        <tr className={"tw-bg-pink-500 tw-bg-opacity-25 tw-rounded-t-lg"}>
          <th scope="col"><span className="tw-sr-only">Select</span></th>
          <th className={thStyle} onClick={() => handleSort("policy")}>Policy{sortIndicator("policy")}</th>
          <th className={thStyle} onClick={() => handleSort("insured")}>Insured{sortIndicator("insured")}</th>
          <th className={thStyle} onClick={() => handleSort("address")}>Address{sortIndicator("address")}</th>
          <th className={thStyle} onClick={() => handleSort("type")}>Type{sortIndicator("type")}</th>
          <th className={thStyle} onClick={() => handleSort("status")}>Status{sortIndicator("status")}</th>
          <th className={thStyle} onClick={() => handleSort("assign")}>Assign{sortIndicator("assign")}</th>
        </tr>
      </thead>
      <tbody>
        {sortedInspections?.map((inspection) =>
          <tr key={inspection.PolicyNo}
            className={"odd:tw-bg-gray-200 hover:tw-bg-primary-light tw-transition tw-duration-200"}
            onMouseOver={() => mouseOverInspection(inspection)}
            onMouseOut={() => mouseOutInspection(inspection)}>
            <td><input type="checkbox" aria-label={`Select ${inspection.PolicyNo}`}
              checked={selectedPolicyNos.has(inspection.PolicyNo)}
              onChange={() => selectInspection(inspection.PolicyNo)} /></td>
            <td><a target={"_blank"} href={`/inspections/${inspection.PolicyNo}/process/pre_r`} rel="noreferrer">{inspection.PolicyNo}</a></td>
            <td>{inspection.Insured}</td>
            <td>{inspection.Address}<br/>{inspection.City}<br/>{inspection.State}, {inspection.Zip}
            </td>
            <td>
              <p className={`tw-text-white tw-p-1 tw-w-8 tw-rounded tw-text-center ${hasMissedAppointment(inspection) ? "tw-rounded-full" : ""}`}
                style={{ backgroundColor: selectedPolicyNos.has(inspection.PolicyNo) ? "blue" : getColorForInspection(inspection, false) }}>{inspection.Type}</p>
              {(inspection.Gated === "yes" || inspection.Gated === "probable") &&
                <img
                  src={`${config.staticPath}/images/${inspection.GateCode ? "gate-amber.png" : "gate-red.png"}`}
                  alt="Gated Community"
                  title={inspection.GateCode ? `Gated (Code: ${inspection.GateCode})` : "Gated Community"}
                  style={{ maxHeight: "26px", marginTop: "4px" }}
                />
              }
            </td>
            {isCompleted(inspection) ?
              <td>Compl. {inspection.CompletedAge} days ago</td> :
              <td>{inspection.PrettyStatus}<br/>{inspection.Age} days old</td>
            }
            <td>{inspection.InspectorName && <>{getInitials(inspection.InspectorName)} <br/></>}
              {!inspection.AppointmentDate.includes("0001-01-01") && parseDateTimeAndChangeToLocalTimezone(inspection.AppointmentDate).toLocaleDateString(undefined,{ month:"short",day:"numeric" })}
              {inspection.AppointmentTime !== "All day" && <><br/>{inspection.AppointmentTime}</>}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}


const Planner = () => {

  const isManager = getRoles().includes("manager");

  const [openInspections, setOpenInspections] = useState([]);
  const [google, setGoogle] = useState(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [mapInfoWindow, setMapInfoWindow] = useState(null);
  const [selectedInspections, setSelectedInspections] = useState([]);
  const selectedPolicyNos = useMemo(() => new Set(selectedInspections.map(i => i.PolicyNo)), [selectedInspections]);
  const selectedInspectionsRef = useRef(selectedInspections);
  const markersRef = useRef(markers);
  const clustererRef = useRef(null);
  const [inspectors, setInspectors] = useState([]);
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [appointmentsFilter, setAppointmentsFilter] = useState("all");
  const [completedFilter, setCompletedFilter] = useState("open");
  const [showAges, setShowAges] = useState({ '10':true,'20':true,'30':true,'60':true,'90':true,'119':true,'120':true });
  const [filteredInspector, setFilteredInspector] = useState(0);
  const [filteredStates, setFilteredStates] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [batchError, setBatchError] = useState(false);
  const [batchSearch, setBatchSearch] = useState("");
  const [loadError, setLoadError] = useState(false);

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const filterSnapshot = useRef(null);
  const [reloadingInspections, setReloadingInspections] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  const [assigningInspections, setAssigningInspections] = useState(false);

  const [filterDescription, setFilterDescription] = useState(null);
  const [drawingMode, setDrawingMode] = useState(false);
  const [drawingManager, setDrawingManager] = useState(null);
  const drawingModeRef = useRef(drawingMode);

  useEffect(() => {
    selectedInspectionsRef.current = selectedInspections;
  }, [selectedInspections]);

  useEffect(() => {
    markersRef.current = markers;
  }, [markers]);

  useEffect(() => {
    drawingModeRef.current = drawingMode;
  }, [drawingMode]);

  useEffect(() => () => { mapInfoWindow?.close(); }, [mapInfoWindow]);
  useEffect(() => () => { drawingManager?.setMap(null); }, [drawingManager]);
  useEffect(() => () => {
    if (map) { window.google.maps.event.clearInstanceListeners(map); }
  }, [map]);

  const mainDiv = useRef();

  // ponytail: pin to v=3.64 — Google removed the Drawing Library (google.maps.drawing)
  // in v3.65.3b (June 2026). Pinning keeps DrawingManager alive; 3.64 will be deleted
  // by Google ~early 2027, so migrate to Terra Draw before then.
  const options = { version: '3.64', libraries: ['drawing', 'geometry'] };
  const loader = new Loader(config.mapsKeyOpenMap, options);

  const hasMissedAppointment = inspection => {
    if (inspection.AppointmentDate.includes("0001-01-01")) {
      return false;
    }
    const appointmentDate = DateTime.fromISO(inspection.AppointmentDate);
    const twoDaysAgo = DateTime.now().minus({ days: 2 });
    return appointmentDate < twoDaysAgo && !["completed","submitted"].includes(inspection.Status);
  };

  const getColorForInspection = (inspection, includeSelection = true) => {

    // if (inspection.PolicyNo === "1501-2401-9630") {
    //   console.log(selectedInspectionsRef.current);
    // }
    if (includeSelection && selectedInspectionsRef.current.some(i => i.PolicyNo === inspection.PolicyNo)) {
      return "blue";
    }

    if (["completed","submitted"].includes(inspection.Status)) {
      return "#00abcd";
    }

    // call getColorForAge, but not with any range of age - split into 5s - so 0-5 days, 6-10, 11-15 etc
    const age = inspection.Age;
    let ageRange = 0;
    if (age <= 10) {
      ageRange = 1;
    } else if (age <= 20) {
      ageRange = 11;
    } else if (age <= 30) {
      ageRange = 21;
    } else {
      ageRange = 31;
    }
    return getColorForAge(ageRange);

  };

  const getColorForAge = age => {
    let r, g, b;

    if (age <= 10) {
      // Interpolate between green (0, 205, 0) and orange (255, 165, 0)
      const localRatio = age / 10;
      r = Math.floor(0 + localRatio * (255 - 0));
      g = Math.floor(205 - localRatio * (205 - 165));
      b = 0;
    } else if (age <= 20) {
      // Interpolate between orange (255, 165, 0) and red (255, 0, 0)
      const localRatio = (age - 10) / 10;
      r = 255;
      g = Math.floor(165 - localRatio * 165);
      b = 0;
    } else if (age <= 30) {
      // Interpolate between red (255, 0, 0) and purple (128, 0, 128)
      const localRatio = (age - 20) / 10;
      r = Math.floor(255 - localRatio * (255 - 128));
      g = 0;
      b = Math.floor(0 + localRatio * 128);
    } else {
      // Interpolate between purple (128, 0, 128) and black (0, 0, 0)
      const localRatio = (age - 30) / 60;
      r = Math.floor(128 - localRatio * 128);
      g = 0;
      b = Math.floor(128 - localRatio * 128);
    }

    return `rgb(${r},${g},${b})`;
  };

  const selectInspection = (policyNo) => {
    const inspection = openInspections.find(i => i.PolicyNo === policyNo);
    if (!inspection) { return; }
    setSelectedInspections(selected => toggleSelection(selected, inspection));
    mapInfoWindow?.close();
  };

  useEffect(() => {
    const byMarkerId = new Map(openInspections.map(i => [`ins-${i.PolicyNo}`, i]));
    const selected = new Set(selectedInspections.map(i => i.PolicyNo));
    markers.forEach(marker => {
      const inspection = byMarkerId.get(marker.content.id);
      if (inspection) { marker.content.style.backgroundColor = selected.has(inspection.PolicyNo) ? "blue" : getColorForInspection(inspection, false); }
    });
  }, [selectedInspections, markers, openInspections]);


  const setMainDivSize = () => {
    if (!mainDiv.current) {
      return;
    }
    const height = window.innerHeight - mainDiv.current.getBoundingClientRect().top - 50;
    mainDiv.current.style.height = window.innerWidth >= 1024 ? `${Math.max(400, height)}px` : "auto";
  };

  const initMap = async () => {
    let localGoogleVar = google;
    if (!localGoogleVar) {
      localGoogleVar = await loader.load();
      setGoogle(localGoogleVar);
    }
    if (!mainDiv.current) { return; }
    const map = new localGoogleVar.maps.Map(document.getElementById('map'), {
      center: { lat: 25.782479, lng: -80.2706578 },
      zoom: 8,
      mapId: '8041c9b566fb264'
    });
    setMap(map);
    setMapInfoWindow(new localGoogleVar.maps.InfoWindow());

    // Initialize Drawing Manager (only if feature flag is enabled)
    let drawingManager = null;
    if (config.mapDrawingTool) {
      drawingManager = new localGoogleVar.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: true,
        drawingControlOptions: {
          position: localGoogleVar.maps.ControlPosition.TOP_CENTER,
          drawingModes: [
            localGoogleVar.maps.drawing.OverlayType.POLYGON,
            localGoogleVar.maps.drawing.OverlayType.CIRCLE,
            localGoogleVar.maps.drawing.OverlayType.RECTANGLE
          ]
        },
        polygonOptions: {
          fillColor: '#0099ff',
          fillOpacity: 0.3,
          strokeColor: '#0099ff',
          strokeWeight: 2,
          clickable: false,
          editable: true,
          draggable: false
        },
        circleOptions: {
          fillColor: '#0099ff',
          fillOpacity: 0.3,
          strokeColor: '#0099ff',
          strokeWeight: 2,
          clickable: false,
          editable: true,
          draggable: false
        },
        rectangleOptions: {
          fillColor: '#0099ff',
          fillOpacity: 0.3,
          strokeColor: '#0099ff',
          strokeWeight: 2,
          clickable: false,
          editable: true,
          draggable: false
        }
      });
    
      drawingManager.setMap(map);
    }
    setDrawingManager(drawingManager);

  };

  useEffect(() => {
    const initData = async () => {
      const inspectors = await apiInspectors.get();
      setInspectors(inspectors.data.result);
    };

    initData();
    initMap();
    window.addEventListener("resize", setMainDivSize);
    setMainDivSize();
    return () => {
      window.removeEventListener("resize", setMainDivSize);
      clustererRef.current?.setMap(null);
      markersRef.current.forEach(marker => { marker.map = null; });
    };
  }, []);

  function ageFilter(inspection) {
    return showAges[ageGroup(inspectionAge(inspection))];
  }

  const doAppointmentsFilter = (inspection) => {
    if (appointmentsFilter === "all") {
      return true;
    }

    if (appointmentsFilter==="hasappt") {
      return !inspection.AppointmentDate.includes("0001-01-01") && !hasMissedAppointment(inspection);
    }

    if (appointmentsFilter==="noappt") {
      return inspection.AppointmentDate.includes("0001-01-01");
    }

    if (appointmentsFilter==="missed") {
      return hasMissedAppointment(inspection);
    }

    return false;

  };

  const updateInspections = async () => {
    const response = await apiInspections.getOpenInspectionsMap(completedFilter, filteredBatches);
    if (response.data.status !== 200) { throw new Error("Could not load inspections"); }
    const inspections = response.data.result || [];
    // Sort by the age used by the filter: time since completion for completed inspections.
    inspections.sort((a, b) => inspectionAge(b) - inspectionAge(a) || a.Zip.localeCompare(b.Zip));
    setLoadError(false);

    setOpenInspections(inspections);
    setSelectedInspections([]);
    updateFilterDescription();
  };

  const availableStates = useMemo(() => {
    if (!openInspections) {return [];}
    return [...new Set(openInspections.map(i => i.State))].sort();
  }, [openInspections]);

  const filteredInspections = useMemo(() => {
    return openInspections?.filter(i => doAppointmentsFilter(i)).
      filter(i => ageFilter(i)).
      filter(i => filteredInspector===0 || i.InspectorID === Number(filteredInspector)).
      filter(i => filteredStates.length === 0 || filteredStates.includes(i.State));
  }, [openInspections, filteredInspector, filteredStates, showAges, appointmentsFilter]);

  // Dependencies must be initialized first, or Babel captures undefined instead of the filtered rows.
  useEffect(() => {
    let cancelled = false;
    updateMapBounds(() => !cancelled);
    setMainDivSize();
    return () => { cancelled = true; };
  }, [map, filteredInspections]);

  // useEffect(() => {
  //   updateInspections(showNoAppointmentsOnly);
  // }, []);

  useEffect(() => {
    // this also runs on page load
    updateInspections().catch(() => setLoadError(true));
    apiInspections.getOpenMapBatches().then(response => {
      if (response.data.status !== 200) { throw new Error("Batch list unavailable"); }
      setBatches(response.data.result || []);
    }).catch(() => setBatchError(true));
  }, []);

  // Handle drawing mode changes
  useEffect(() => {
    if (drawingManager) {
      if (drawingMode) {
        drawingManager.setOptions({
          drawingControl: true,
          drawingMode: google.maps.drawing.OverlayType.POLYGON
        });
        
        // Add event listener for when a shape is completed
        const shapeCompleteListener = drawingManager.addListener('overlaycomplete', handleShapeComplete);
        
        // Add event listener for when drawing mode changes (when user selects different tool)
        const drawingModeChangedListener = drawingManager.addListener('drawingmode_changed', () => {
          const currentMode = drawingManager.getDrawingMode();
          // Disable map dragging only when actively drawing a polygon
          if (map && currentMode === google.maps.drawing.OverlayType.POLYGON) {
            map.setOptions({ draggable: false });
          } else if (map) {
            map.setOptions({ draggable: true });
          }
        });
        
        return () => {
          google.maps.event.removeListener(shapeCompleteListener);
          google.maps.event.removeListener(drawingModeChangedListener);
          // Re-enable dragging when exiting drawing mode
          if (map) {
            map.setOptions({ draggable: true });
          }
        };
      } else {
        drawingManager.setOptions({
          drawingControl: false,
          drawingMode: null
        });
        // Ensure map dragging is enabled when not in drawing mode
        if (map) {
          map.setOptions({ draggable: true });
        }
      }
    }
  }, [drawingMode, drawingManager, map]);


  async function doCancelAppointment(policyNo, address) {
    if (!window.confirm(`Are you sure you want to cancel the appointment for the selected inspection - ${policyNo} / ${address}`)) {
      return;
    }
    await apiInspections.cancelAppointment(policyNo);
    updateInspections();
  }

  function createMarker(AdvancedMarkerElement, inspection, clustered) {

    const markerTag = document.createElement("div");

    markerTag.className = "inspection-map-tag inspection-map-tag-global";

    const tagSvgRaw = (color,textColor,label) => `
<svg width="40px" height="40px" viewBox="0 0 329 329" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" xmlns:serif="http://www.serif.com/" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;">
    <g transform="matrix(1,0,0,1,-299.417,-181.468)">
        <g transform="matrix(0.591565,0.590658,-0.683963,0.685014,414.882,-180.89)">
            <rect x="348.406" y="228.564" width="275.224" height="241.594" style="fill:${color};"/>
        </g>
        <g transform="matrix(1.4404,0,0,1.4404,-212.537,-142.489)">
            <g transform="matrix(48,0,0,48,514.338,345.906)">
            </g>
            <text x="430px" y="360px" style="font-size:64px;fill:white;">${label}</text>
        </g>
    </g>
</svg>
`;

    if (hasMissedAppointment(inspection)) {
      markerTag.classList.add("inspection-map-tag-missed");
    }

    const spanTag = document.createElement("span");
    spanTag.textContent = inspection.Type;
    markerTag.appendChild(spanTag);
    markerTag.id = `ins-${inspection.PolicyNo}`;
    markerTag.style.backgroundColor = getColorForInspection(inspection);
    markerTag.style.setProperty("--open-map-arrow-color", inspection.Type === "UE" ? "green" : "yellow");

    markerTag.style.padding = "8px 8px";
    markerTag.style.fontSize = "0.8rem";

    const marker = new AdvancedMarkerElement({
      map: clustered ? null : map,
      position: { lat: inspection.Lat, lng: inspection.Lng },
      content: markerTag,
    });

    marker.addListener("click", () => {
      
      // Don't handle marker clicks when in drawing mode
      if (drawingModeRef.current) {
        return;
      }

      if (selectedInspectionsRef.current.length > 0) {
        selectInspection(inspection.PolicyNo);
        return;
      }

      window.selectInspectionFunc = selectInspection;

      let content = "";
      content = content + `<div>`;
      content = content + `<p class="tw-text-xl"><a class="" target="_blank" href="/inspections/${inspection.PolicyNo}/process/pre_r"}><strong class="tw-text-primary-500">${inspection.PolicyNo}</strong></a></p>`;
      content = content + `<p>${inspection.Insured}</p>`;
      content = content + `<p>${inspection.Address}<br/>${inspection.City}, ${inspection.County}, ${inspection.State} ${inspection.Zip}</p>`;
      content = content + `<br/`;
      content = content + `<p>Inspection Type: ${inspection.Type}</p>`;
      content = content + `<p>${inspection.Age} days old</p>`;
      if (filteredBatches.length === 1) {
        const batch = batches.find(b => b.id === filteredBatches[0]);
        content = content + `<p class="tw-mt-2 tw-font-semibold">Out-of-area batch #${filteredBatches[0]} — ${batch?.returned ? "returned" : "return not confirmed"}.</p>`;
      } else if (filteredBatches.length > 1) {
        content = content + `<p class="tw-mt-2 tw-font-semibold">In a selected out-of-area batch.</p>`;
      } else if (inspection.UnconfirmedBatchID > 0) {
        content = content + `<p class="tw-mt-2 tw-font-semibold">Out-of-area batch #${inspection.UnconfirmedBatchID} — return not confirmed.</p>`;
      }
      if (inspection.Gated === "yes" || inspection.Gated === "probable") {
        content = content + `<p>🏛️ Gated Community${inspection.GateCode ? ` (Code: ${inspection.GateCode})` : ""}</p>`;
      }
      content = content + `<br/`;
      content = content + `<p>Status: <strong>${inspection.PrettyStatus}</strong></p>`;
      content = content + `<p>Assigned to: ${inspection.InspectorName ? inspection.InspectorName : "n/a"}</p>`;
      content = content + `<p>Appointment Date: ${!inspection.AppointmentDate.includes("0001-01-01") ? parseDateTimeAndChangeToLocalTimezone(inspection.AppointmentDate).toLocaleDateString(undefined,{ month: 'short', day: 'numeric' }):"n/a"}</p>`;
      content = content + `<div class="tw-mt-4 tw-flex tw-justify-cente tw-gap-4">`;
      content = content + `<button class="tw-text-white tw-px-4 tw-py-2 tw-bg-primary-500 tw-uppercase" onclick="selectInspectionFunc('${inspection.PolicyNo}')">Select</button>`;
      if (isManager) {
        const hasAppointment = !inspection.AppointmentDate.includes("0001-01-01");
        content = content + `<button disabled=${hasAppointment?"false":"true"} class="tw-px-4 tw-py-2 tw-uppercase ${!hasAppointment ? "tw-bg-gray-300 tw-cursor-default" : "tw-bg-primary-500 tw-text-white"}" onclick="window.doCancelAppointmentFunc('${inspection.PolicyNo}','${inspection.Address}')">Cancel Appt.</button>`;
      }
      content = content + `</div>`;

      window.doCancelAppointmentFunc = doCancelAppointment;

      if (mapInfoWindow) {
        mapInfoWindow.setContent(content);
        mapInfoWindow.open({ anchor: marker, map });
      } else {
        console.error("mapInfoWindow not set");
      }
    });

    return marker;
  }

  const updateMapBounds = async (isCurrent) => {
    if (!map) {
      return;
    }
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
    if (!isCurrent()) { return; }
    clustererRef.current?.setMap(null);
    clustererRef.current = null;
    markersRef.current.forEach(marker => { marker.map = null; });
    const markerList = [];
    const clustered = filteredInspections.length > 2000;
    if (map && openInspections) {
      const bounds = new window.google.maps.LatLngBounds();
      // reverse order to show oldest first so that the newest pins are on top
      const reversedInspections = filteredInspections.slice().reverse();
      for (const inspection of reversedInspections) {
        const marker = createMarker(AdvancedMarkerElement, inspection, clustered);
        markerList.push(marker);
        bounds.extend(marker.position);
      }
      if (markerList.length) { map.fitBounds(bounds); }
      markersRef.current = markerList;
      setMarkers(markerList);
      if (clustered) {
        clustererRef.current = new MarkerClusterer({ map, markers: markerList });
      }
    }
  };

  function mouseOverInspection(inspection) {
    // make map pin expand
    const pin = document.getElementById(`ins-${inspection.PolicyNo}`);
    if (pin) {
      pin.classList.add("tw-transform","tw-scale-200","z-10");
    }
  }

  function mouseOutInspection(inspection) {
    // make map pin shrink
    const pin = document.getElementById(`ins-${inspection.PolicyNo}`);
    if (pin) {
      pin.classList.remove("tw-transform","tw-scale-200","z-10");
    }
  }

  const assignClick = async () => {
    // confirm assignment
    if (!(selectedInspector || selectedDate)) {
      alert("Please select an inspector and / or date");
      return;
    }
    const inspectorName = inspectors.find(i => i.id === Number(selectedInspector))?.name;
    if (selectedDate && inspectorName) {
      if (!confirm(`Assign ${selectedInspections.length} inspections to ${inspectorName} with appointment on ${selectedDate}?`)) {
        return;
      }
    } else if (selectedDate && !inspectorName) {
      if (!confirm(`Set appointment for ${selectedInspections.length} inspections on ${selectedDate}?`)) {
        return;
      }
    } else {
      if (!confirm(`Assign ${selectedInspections.length} inspections to ${inspectorName}?`)) {
        return;
      }
    }

    setAssigningInspections(true);
    await apiInspections.assignInspectionsFromMap(Number(selectedInspector), selectedDate, selectedInspections.map(i => i.PolicyNo));
    clearSelection();
    updateInspections();
    setAssigningInspections(false);
  };

  const unAssignClick = async () => {
    // confirm assignment
    if (!confirm(`Unassign ${selectedInspections.length} inspections?`)) {
      return;
    }

    setAssigningInspections(true);
    await apiInspections.unAssignInspectionsFromMap(selectedInspections.map(i => i.PolicyNo));
    clearSelection();
    updateInspections();
    setAssigningInspections(false);
  };

  const cancelApptClick = async () => {
    if (!confirm(`Cancel ${selectedInspections.length} appointments?`)) {
      return;
    }

    setAssigningInspections(true);
    await apiInspections.cancelAppointments(selectedInspections.map(i => i.PolicyNo));
    clearSelection();
    updateInspections();
    setAssigningInspections(false);
  };

  const unAssignAndCancelClick = async () => {
    if (!confirm(`Unassign and cancel appointment for ${selectedInspections.length} inspections?`)) {
      return;
    }

    setAssigningInspections(true);
    await apiInspections.unAssignInspectionsFromMap(selectedInspections.map(i => i.PolicyNo));
    await apiInspections.cancelAppointments(selectedInspections.map(i => i.PolicyNo));
    clearSelection();
    updateInspections();
    setAssigningInspections(false);
  };

  // const sortedInspections = openInspections?.toSorted((a,b) => {
  //   // sort by age, then by zip
  //   // put any selected inspection at the top, maintaining the age and zip
  //   if (selectedInspectionsRef.current.includes(a) && !selectedInspectionsRef.current.includes(b)) {
  //     return -1;
  //   }
  //   if (!selectedInspectionsRef.current.includes(a) && selectedInspectionsRef.current.includes(b)) {
  //     return 1;
  //   }
  //
  //   if (a.Age === b.Age) {
  //     return a.Zip - b.Zip;
  //   }
  //   return b.Age - a.Age;
  //
  // });

  const clearSelection = () => {
    setSelectedInspections([]);
  };

  const handleShapeComplete = (event) => {
    const shape = event.overlay;
    const shapeType = event.type;
    
    // Find all inspections within the drawn shape
    const inspectionsToSelect = [];
    
    filteredInspections?.forEach(inspection => {
      const inspectionLatLng = new google.maps.LatLng(inspection.Lat, inspection.Lng);
      let isInside = false;
      
      if (shapeType === google.maps.drawing.OverlayType.POLYGON) {
        isInside = google.maps.geometry.poly.containsLocation(inspectionLatLng, shape);
      } else if (shapeType === google.maps.drawing.OverlayType.CIRCLE) {
        const distance = google.maps.geometry.spherical.computeDistanceBetween(
          inspectionLatLng, 
          shape.getCenter()
        );
        isInside = distance <= shape.getRadius();
      } else if (shapeType === google.maps.drawing.OverlayType.RECTANGLE) {
        const bounds = shape.getBounds();
        isInside = bounds.contains(inspectionLatLng);
      }
      
      if (isInside) {
        inspectionsToSelect.push(inspection);
      }
    });
    
    // Select all inspections found within the shape in a single batch
    const alreadySelected = new Set(selectedInspectionsRef.current.map(i => i.PolicyNo));
    const newSelections = inspectionsToSelect.filter(i => !alreadySelected.has(i.PolicyNo));

    if (newSelections.length > 0) {
      setSelectedInspections(prev => [...prev, ...newSelections]);
    }
    
    // Remove the drawn shape from the map
    shape.setMap(null);
    
    // Exit drawing mode
    setDrawingMode(false);
  };

  function getLabelForAgeCheck(age) {
    switch (age) {
    case '10':
      return "0-10 days";
    case '20':
      return "11-20 days";
    case '30':
      return "21-30 days";
    case '60': return "31-60 days";
    case '90': return "61-90 days";
    case '119': return "91-119 days";
    case '120': return "120+ days";
    }
  }

  const ageFilterItems = ['10','20','30','60','90','119','120'];

  const inspectorsWithInspections = inspectors.filter((i) => {
    for (const inspection of openInspections) {
      if (inspection.InspectorID === i.id) {
        return true;
      }
    }
  });

  const openFilterDialog = () => {
    filterSnapshot.current = { appointmentsFilter, completedFilter, showAges, filteredStates, filteredBatches, filteredInspector };
    setFilterDialogOpen(true);
  };

  const cancelFilterChange = () => {
    if (reloadingInspections) { return; }
    const saved = filterSnapshot.current;
    if (saved) {
      setAppointmentsFilter(saved.appointmentsFilter);
      setCompletedFilter(saved.completedFilter);
      setShowAges(saved.showAges);
      setFilteredStates(saved.filteredStates);
      setFilteredBatches(saved.filteredBatches);
      setFilteredInspector(saved.filteredInspector);
    }
    setFilterDialogOpen(false);
  };

  const applyFilterChange = async () => {
    setReloadingInspections(true);
    try {
      await updateInspections();
      setFilterDialogOpen(false);
    } catch (err) {
      setLoadError(true);
    } finally {
      setReloadingInspections(false);
    }
  };

  const updateFilterDescription = () => {
    let parts = [];
    if (appointmentsFilter==="missed") {
      parts.push("Missed Appointment (>2 days no complete)");
    } else if (appointmentsFilter==="noappt") {
      parts.push("No Appointment");
    } else if (appointmentsFilter==="hasappt") {
      parts.push("With Appointment (not missed)");
    }
    if (completedFilter==="completed") {
      parts.push("Completed Inspections");
    }
    if (completedFilter === "both") { parts.push("Open and completed"); }
    if (ageFilterItems.every(age => showAges[age])) {
      parts.push("All Ages");
    } else {
      parts.push("Ages: " + Object.keys(showAges).filter(k => showAges[k]).map(k => getLabelForAgeCheck(k)).join(", "));
    }
    if (filteredStates.length) {
      parts.push(`States: ${filteredStates.join(", ")}`);
    }
    if (filteredBatches.length) { parts.push(`Out-of-area batches: ${filteredBatches.map(id => `#${id}`).join(", ")}`); }
    if (filteredInspector>0) {
      parts.push(`Inspector: ${inspectors.find(i => i.id === Number(filteredInspector))?.name}`);
    }
    setFilterDescription(parts.join(" / "));
  };

  const tableProps = { selectedPolicyNos, selectInspection, mouseOverInspection, mouseOutInspection, getColorForInspection, hasMissedAppointment };

  return (
    <>
      <div>
        <h1>Global Open Inspections</h1>
        {loadError && <p role="alert" className="tw-text-red-600">Could not load inspections. Please try again.</p>}

        <div className={"tw-flex tw-flex-wrap tw-gap-8 tw-mb-4 tw-items-baseline"}>

          <FSButton onClick={openFilterDialog}>Filters...</FSButton>

          <p>{filterDescription}</p>
          
          {config.mapDrawingTool && drawingMode && <p className="tw-text-blue-600 tw-font-semibold">Drawing mode active - draw shapes to select inspections</p>}

        </div>

        <div className={"tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2 tw-items-center"}>
          {selectedInspections.length===0 && <p className={"tw-text-xl"}>Displayed Inspections - {filteredInspections?.length}</p>}
          <ColorKey colors={[
            {
              "title": `Selected`,
              "color": "blue"
            },
            {
              "title": "Completed",
              "color": "#00acdc"
            },
            {
              "title": "Aged 0-10 days",
              "color": getColorForAge(1)
            },
            {
              "title": "Aged 11-20 days",
              "color": getColorForAge(11)
            },
            {
              "title": "Aged 21-30 days",
              "color": getColorForAge(21)
            },
            {
              "title": "Aged 31+ days (same colour)",
              "color": getColorForAge(31)
            },
            {
              "title":"Missed Appointment",
              "color":"black",
              "shape":"circle"
            }
          ]}/>
        </div>
        <div ref={mainDiv} className="open-map-layout tw-my-4">
          <div className="open-map-list tw-overflow-y-auto">
            {selectedInspections.length > 0 &&
                <div className={"tw-mb-4"}>
                  <div className={"tw-flex tw-items-center tw-gap-8"}>
                    <p className={"tw-text-xl"}>Selected {selectedInspections.length === 1 ? "inspection" : "inspections"} - {selectedInspections.length}</p>
                    <FSButton onClick={clearSelection}>clear</FSButton>
                  </div>
                  <InspectionsTable inspections={selectedInspections} {...tableProps}/>

                  <div className={"tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4"}>
                    <div className={"tw-flex tw-gap-2 tw-items-baseline"}>
                      <input className="tw-rounded-lg" type="date" value={selectedDate}
                        onChange={e => setSelectedDate(e.target.value)}/>
                    </div>
                    <select className="tw-rounded-lg tw-flex-grow" onChange={e => setSelectedInspector(e.target.value)}>
                      <option value="0">Select Inspector</option>
                      {inspectors.map((inspector) =>
                        <option key={inspector.id} value={inspector.id}>{inspector.name}</option>
                      )}
                    </select>
                    <FSButton disabled={!(selectedInspector > 0 || selectedDate)} submitting={assigningInspections}
                      onClick={assignClick}>Apply</FSButton>
                  </div>
                  <div className={"tw-flex tw-flex-wrap tw-gap-2 tw-justify-between"}>
                    <FSButton submitting={assigningInspections} onClick={unAssignClick}>Unassign</FSButton>
                    <FSButton submitting={assigningInspections} onClick={cancelApptClick}>Cancel Appt.</FSButton>
                    <FSButton submitting={assigningInspections} onClick={unAssignAndCancelClick}>Unassign & Cancel
                      Appt.</FSButton>
                    {isManager && <FSButton onClick={() => setContactDialogOpen(true)} icon={<i className="fa fa-comment-alt"/>}>Contact</FSButton>}
                  </div>

                </div>
            }

            {selectedInspections.length>0 && <p className={"tw-text-xl"}>Displayed Inspections - {filteredInspections?.length}</p>}
            <InspectionsTable inspections={filteredInspections} {...tableProps}/>
          </div>
          <div className="open-map-map tw-relative">
            <div id={"map"}
              className="tw-shadow tw-overflow-hidden tw-rounded-xl tw-w-full tw-h-full">
            </div>
            
            {/* Drawing Mode Button Overlay */}
            {config.mapDrawingTool && 
              <div className="tw-absolute tw-top-12 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-z-10">
                <button
                  onClick={() => setDrawingMode(!drawingMode)}
                  className={`tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-shadow-sm hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
                    drawingMode ? "tw-bg-blue-500 tw-text-white tw-border-blue-500 hover:tw-bg-blue-600" : "tw-text-gray-700"
                  }`}
                  style={{ fontFamily: 'Roboto, Arial, sans-serif' }}
                >
                  {drawingMode ? "Exit Drawing" : "Drawing Mode"}
                </button>
              </div>
            }
          </div>
        </div>

      </div>

      <Dialog fullWidth maxWidth="md" open={filterDialogOpen} onClose={cancelFilterChange}>
        <DialogTitle>Filters</DialogTitle>
        <DialogContent>
          {loadError && <p role="alert" className="tw-text-red-600 tw-mb-3">Could not load inspections. Please try again.</p>}
          <fieldset disabled={reloadingInspections} className="open-map-filter-grid">
            <p className={"tw-font-bold"}>Appointments</p>
            <div className={"tw-flex tw-flex-wrap tw-gap-4"}>
              <label>
                <input type={"radio"} name="appointmentsradio" value={"all"} checked={appointmentsFilter === "all"}
                  onClick={() => setAppointmentsFilter("all")}/>
                All
              </label>
              <label>
                <input type={"radio"} name="appointmentsradio" value={"noappt"} checked={appointmentsFilter === "noappt"}
                  onClick={() => setAppointmentsFilter("noappt")}/>
                No appt.
              </label>
              <label>
                <input type={"radio"} name="appointmentsradio" value={"hasappt"} checked={appointmentsFilter === "hasappt"}
                  onClick={() => setAppointmentsFilter("hasappt")}/>
                Has appt.
              </label>
              <label>
                <input type={"radio"} name="appointmentsradio" value={"missed"} checked={appointmentsFilter === "missed"}
                  onClick={() => setAppointmentsFilter("missed")}/>
                Missed appt.
              </label>

            </div>
            <p className={"tw-font-bold"}>Open or Completed?</p>
            <div className={"tw-flex tw-flex-wrap tw-gap-4"}>
              <label>
                <input type={"radio"} name="openorcompleted" value={"open"} checked={completedFilter === "open"}
                  onClick={() => setCompletedFilter("open")}/>
                Open
              </label>
              <label>
                <input type={"radio"} name="openorcompleted" value={"completed"}
                  checked={completedFilter === "completed"} onClick={() => setCompletedFilter("completed")}/>
                Completed
              </label>
              <label>
                <input type="radio" name="openorcompleted" value="both"
                  checked={completedFilter === "both"} onClick={() => setCompletedFilter("both")}/>
                Both
              </label>
              <span className="tw-text-xs tw-text-gray-600">Completed inspections from the last 30 days.</span>
            </div>
            <p className={"tw-font-bold"}>Age</p>
            <div className={"tw-flex tw-flex-wrap tw-gap-4"}>
              {ageFilterItems.map((age) =>
                <label className={"tw-pr-2"} key={age}>
                  <input className={"tw-pl-2"} type={"checkbox"} checked={showAges[age]}
                    onClick={(e) => setShowAges({ ...showAges, [age]: e.target.checked })}/>
                  {getLabelForAgeCheck(age)}
                </label>
              )}
            </div>
            <p className={"tw-font-bold"}>States</p>
            <div className="tw-flex tw-flex-wrap tw-gap-3" role="group" aria-label="States (none selected means all)">
              {availableStates.map(state => <label key={state}>
                <input type="checkbox" checked={filteredStates.includes(state)} onChange={e => setFilteredStates(
                  e.target.checked ? [...filteredStates, state] : filteredStates.filter(s => s !== state)
                )}/>{state}
              </label>)}
              <span className="tw-text-xs tw-text-gray-600">None selected means all states.</span>
            </div>
            <p className="tw-font-bold">Out of Area batches</p>
            <div>
              {batchError ? <p role="alert" className="tw-text-red-600">Could not load batches.</p> : batches.length === 0
                ? <p className="tw-text-sm tw-text-gray-500">No batches available.</p>
                : <details className="tw-border tw-border-gray-300 tw-rounded-md tw-bg-white">
                  <summary className="tw-cursor-pointer tw-px-3 tw-py-2 tw-text-sm">
                    {filteredBatches.length ? `${filteredBatches.length} ${filteredBatches.length === 1 ? "batch" : "batches"} selected` : "Select batches"}
                  </summary>
                  <div className="tw-border-t tw-border-gray-200 tw-p-3">
                    <input type="search" aria-label="Search batches" placeholder="Search by batch number or status"
                      value={batchSearch} onChange={e => setBatchSearch(e.target.value)}
                      className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2 tw-mb-2 tw-text-sm" />
                    <div className="tw-flex tw-flex-col tw-gap-2 tw-max-h-40 tw-overflow-y-auto" role="group" aria-label="Out of Area batches">
                      {batches.filter(batch => `#${batch.id} ${batch.returned ? "returned" : "unconfirmed"}`.toLowerCase().includes(batchSearch.trim().toLowerCase())).map(batch =>
                        <label key={batch.id} className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm">
                          <input type="checkbox" checked={filteredBatches.includes(batch.id)} onChange={e => setFilteredBatches(
                            e.target.checked ? [...filteredBatches, batch.id] : filteredBatches.filter(id => id !== batch.id)
                          )}/>
                          #{batch.id} — {batch.returned ? "returned" : "unconfirmed"}
                        </label>)}
                      {!batches.some(batch => `#${batch.id} ${batch.returned ? "returned" : "unconfirmed"}`.toLowerCase().includes(batchSearch.trim().toLowerCase())) &&
                        <p className="tw-text-sm tw-text-gray-500">No matching batches.</p>}
                    </div>
                  </div>
                </details>}
              <p className="tw-text-xs tw-text-gray-600 tw-mt-2">Select batches to show only their inspections. With none selected, returned batches are hidden.</p>
            </div>
            <p className={"tw-font-bold"}>Inspector</p>
            <div>
              <select className="tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-2" value={filteredInspector}
                onChange={e => setFilteredInspector(Number(e.target.value))}>
                <option value={0}>All</option>
                {inspectorsWithInspections.map((inspector) =>
                  <option key={inspector.id} value={inspector.id}>{inspector.name}</option>
                )}
              </select>
            </div>
          </fieldset>

        </DialogContent>
        <DialogActions>
          <FSButton submitting={reloadingInspections} onClick={() => applyFilterChange()}>Apply</FSButton>
          <FSButton disabled={reloadingInspections} onClick={cancelFilterChange}>Cancel</FSButton>
        </DialogActions>
      </Dialog>

      <BatchContactDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        inspections={selectedInspections}
      />

    </>
  );

};

export default Planner;